import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { DrawCreateEvent } from "@mapbox/mapbox-gl-draw";
import staticMode from "@mapbox/mapbox-gl-draw-static-mode";
import { CloudUpload, HighlightAlt } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button } from "@mui/material";
import { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import { defer, isEmpty } from "lodash";
import { FeatureIdentifier } from "mapbox-gl";
import { passing_draw_polygon as passingDrawPolygon } from "mapbox-gl-draw-passing-mode";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import { multiProjectsSlice, useGetMultiProjectStatisticsQuery, useLazyGetMultiProjectQuery, useUpdateMultiProjectMutation } from "fond/api";
import DrawHandler from "fond/draw/DrawHandler";
import readOnlySelectMode from "fond/draw/modes/base/readOnlySelect";
import { HistoryContext, useHistory } from "fond/history";
import { MapContext } from "fond/map/MapProvider";
import { MAX_NUM_ADDRESSES } from "fond/project/polygon";
import { EditMode, setEditMode } from "fond/project/redux";
import { bbox, feature as toFeature, featureCollection, multiPolygon } from "fond/turf";
import { MultiProject, MultiProjectAreaImportMethod, UpdateMultiProjectAreaEntry } from "fond/types";
import { isAnyPolygon, isPolygon, isValuePolygon } from "fond/types/geojson";
import { makeUuid, toBBox } from "fond/utils";
import { layerPalette } from "fond/utils/colors";
import { useAppDispatch } from "fond/utils/hooks";
import { getItem, setItem } from "fond/utils/localStorage";
import { generateUnusedName } from "fond/utils/naming";
import { BlockSpinner, useStackedNavigationContext } from "fond/widgets";
import StackedNavigationHeader from "fond/widgets/StackedNavigation/StackedNavigationHeader";
import AutoCombineWorker from "fond/workers/carve?worker";

import areaDrawStyles from "./areaDrawStyles";
import AreaList from "./AreaList";
import SubareaConfigPanel from "./SubareaConfigPanel";
import { carveToolbarConfig } from "./toolbar";
import { useBlockGroups } from "./useBlockGroups";

export type SubareaProperties = {
  id: string;
  name: string;
  minCount: number | null;
  exactCount: number | null;
  importMethod: MultiProjectAreaImportMethod;
  color: string;
  censusBlockGroupIds: string[];
};

export type SubareaFeature = Feature<Polygon | MultiPolygon, SubareaProperties>;

const modes = {
  simple_select: readOnlySelectMode,
  passing_draw_polygon: passingDrawPolygon,
  static: staticMode,
};

const sources: FeatureIdentifier[] = [{ source: "multiProject-source" }];

interface AreaDrawPanelProps {
  multiProject: MultiProject;
}

const AreaDrawPanel: React.FC<AreaDrawPanelProps> = ({ multiProject }) => {
  const dispatch = useAppDispatch();
  const { data: statistics } = useGetMultiProjectStatisticsQuery(multiProject.ID);
  const { goBack, clear } = useStackedNavigationContext();
  const { map, drawControl, setDrawMode, isDrawing } = useContext(MapContext);
  const { set: setHistory, state: historyState, clear: clearHistory } = useHistory<FeatureCollection>(HistoryContext);
  const [getMultiProject] = useLazyGetMultiProjectQuery();
  const [updateMultiProject] = useUpdateMultiProjectMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const [autoCarving, setAutoCarving] = useState<"Idle" | "Carving" | "Processing">("Idle");
  const {
    isLoading: isDatasetLoading,
    blockGroups,
    resetBlockGroups,
  } = useBlockGroups(multiProject?.Account.ID ?? null, multiProject?.Boundary ?? null);
  const workerRef = useRef<Worker | null>(null);
  const [rowData, setRowData] = useState<SubareaProperties[] | null>(null);
  const [subareaMode, setSubareaMode] = useState<"raw" | "combine">(getItem(`state.project.projects[${multiProject.ID}].subareaMode`, "combine"));
  const [combineTargetSize, setCombineTargetSize] = useState<number>(getItem(`state.project.projects[${multiProject.ID}].combineTargetSize`, 6_000));
  const requestIdRef = useRef<number>(0);

  /**
   * Get the multiproject Subareas & convert them to multiple polygons ready to load as
   * the initial draw features.
   */
  const initialFeatures = useMemo(() => {
    if (multiProject?.Areas && multiProject.Areas.length > 0) {
      return multiProject?.Areas.map((area) =>
        toFeature(
          area.Boundary,
          {
            id: area.ID,
            name: area.Name,
            importMethod: area.ImportMethod,
            exactCount: statistics?.Areas.find((a) => a.Area.ID === area.ID)?.AddressCounts.AddressCount ?? 0,
            minCount: null,
            color: area.Style.Color,
          },
          { id: `${area.ID}` }
        )
      );
    }

    return [];
  }, [multiProject?.Areas, statistics?.Areas]);

  const getRowData = useCallback(() => {
    return drawControl.current && map?.hasControl(drawControl.current)
      ? (drawControl.current?.getAll() as FeatureCollection<Polygon, SubareaProperties>).features
          .filter(isAnyPolygon)
          .filter(isValuePolygon)
          .map(({ properties }) => properties)
      : [];
  }, [drawControl, map]);

  /**
   * Sync the drawing features with the current state of history as the user
   * clicks undo/redo.
   */
  useEffect(() => {
    if (isDrawing) {
      if (isEmpty(historyState)) {
        drawControl.current?.set(featureCollection(initialFeatures));
      } else {
        drawControl.current?.set(historyState);
      }
      setRowData(getRowData());
    }
  }, [initialFeatures, isDrawing, drawControl, historyState, getRowData]);

  useEffect(() => {
    return () => {
      clearHistory();
      setDrawMode("no_feature");
    };
  }, [clearHistory, setDrawMode]);

  const handleAutoCarveComplete = useCallback(
    (rawFeatures: Feature<Polygon | MultiPolygon, { addressCount: number; name?: string; ids: string[] }>[], requestId: number) => {
      // Ignore stale responses
      if (requestId !== requestIdRef.current) {
        return;
      }

      const names: string[] = [];

      const features = rawFeatures.map((f, i) => {
        const id = makeUuid();
        const name = generateUnusedName(f.properties.name ?? "Subarea", names);
        names.push(name);

        return toFeature(
          f.geometry,
          {
            id,
            name,
            importMethod: "area_select_underground",
            exactCount: f.properties.addressCount,
            minCount: null,
            color: layerPalette[i % layerPalette.length],
            censusBlockGroupIds: f.properties.ids,
          },
          { id }
        );
      });

      drawControl.current?.set(featureCollection(features));
      if (drawControl.current?.getAll()) setHistory(drawControl.current?.getAll());
      setAutoCarving("Idle");
    },
    [drawControl, setHistory]
  );

  useEffect(() => {
    const autoCombineWorker = new AutoCombineWorker();

    autoCombineWorker.onmessage = (event) => {
      handleAutoCarveComplete(event.data.features, event.data.requestId);
    };

    workerRef.current = autoCombineWorker;

    // Clean up the worker when the component unmounts
    return () => {
      workerRef.current?.terminate();
    };
  }, [handleAutoCarveComplete]);

  // Callback to update the boundary
  const updateBoundary = useCallback(
    async (feature: Feature<Polygon | MultiPolygon>) => {
      dispatch(
        multiProjectsSlice.util.updateQueryData("getMultiProject", multiProject.ID, (draft) => {
          draft.Boundary = feature.geometry;
        })
      );

      setAutoCarving("Carving");
      setIsDirty(true);

      // Shift the maps focus to the boundary, this is important when the user
      // uploads a geojson file that could be anywhere within the map.
      defer(() => {
        map?.fitBounds(toBBox(bbox(feature)), { animate: false, padding: 100 });
      });
    },
    [dispatch, map, multiProject.ID]
  );

  // Kick off an auto-carve whenever the block groups or target size change.
  useEffect(() => {
    // If the user hasn't made any changes, don't auto-carve. This prevents a situation
    // where opening the page causes an auto-carve to occur, overwriting existing working.
    if (!isDirty || !blockGroups || !workerRef.current) return;

    setAutoCarving("Processing");

    // Increment request ID to invalidate any in-flight requests
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    // Offload processing to the worker
    workerRef.current.postMessage({
      features: blockGroups.filter(isAnyPolygon),
      maxAddresses: subareaMode === "raw" ? 0 : combineTargetSize,
      requestId: currentRequestId,
    });
  }, [blockGroups, subareaMode, combineTargetSize, isDirty]);

  const handleBoundaryChange = useCallback(
    async (event: DrawCreateEvent) => {
      event.features.filter(isAnyPolygon).forEach((feature) => {
        updateBoundary(feature);
      });
    },
    [updateBoundary]
  );

  useEffect(() => {
    map?.on("draw.passing-create", handleBoundaryChange);

    return () => {
      map?.off("draw.passing-create", handleBoundaryChange);
    };
  }, [map, handleBoundaryChange]);

  const handleBack = async () => {
    // Manually refresh the multiproject data to restore to saved version.
    await getMultiProject(multiProject.ID, false).unwrap();
    goBack();
    dispatch(setEditMode(EditMode.none));
  };

  const onReset = useCallback(() => {
    drawControl.current?.set(featureCollection(initialFeatures));
    clearHistory();
    resetBlockGroups();
    setSubareaMode(getItem(`state.project.projects[${multiProject.ID}].subareaMode`, "combine"));
    setCombineTargetSize(getItem(`state.project.projects[${multiProject.ID}].combineTargetSize`, 6_000));
    setAutoCarving("Idle");
  }, [clearHistory, drawControl, initialFeatures, multiProject.ID, resetBlockGroups]);

  const handleSubareaConfigChange = useCallback((config: { mode: "raw" } | { mode: "combine"; value: number }) => {
    setSubareaMode(config.mode);
    if (config.mode === "combine") {
      setCombineTargetSize(config.value);
    }
    setIsDirty(true);
  }, []);

  /**
   * Handles importing the currently draw features and updating the multiProject
   * with the new area boundaries.
   */
  const handleOnImport = () => {
    setIsSaving(true);
    setItem(`state.project.projects[${multiProject.ID}].subareaMode`, subareaMode);
    setItem(`state.project.projects[${multiProject.ID}].combineTargetSize`, combineTargetSize);
    if (drawControl.current?.getMode() === "draw_polygon") {
      // Exit drawing mode & trash any partially drawn features
      drawControl.current.trash();
    }
    const importFeatures = (drawControl.current?.getAll().features.filter(isAnyPolygon) || []) as Feature<
      Polygon | MultiPolygon,
      SubareaProperties
    >[];
    const boundary = importFeatures.length > 0 ? multiProject?.Boundary : null;

    const areas: UpdateMultiProjectAreaEntry[] = importFeatures.map((feat) => {
      const newFeature = isPolygon(feat) ? multiPolygon([feat.geometry.coordinates]).geometry : (feat.geometry as MultiPolygon);

      return {
        Name: feat.properties.name,
        Boundary: newFeature,
        ImportMethod: feat.properties.importMethod,
        Style: {
          Color: feat.properties.color,
        },
        CensusBlockGroups: feat.properties.censusBlockGroupIds?.map((id) => ({ ID: id })) || null,
      };
    });

    analytics.track("project.market-lens.area-draw.finish", { projectId: multiProject.ID, areaCount: areas.length });
    updateMultiProject({ ID: multiProject.ID, Areas: areas, Boundary: boundary }).then(() => {
      setDrawMode("no_feature");
      dispatch(setEditMode(EditMode.none));
      clear();
    });
  };

  // The DrawHandler can crash if the user is fast-fingered and tries to start drawing before the map had loaded.
  if (!map || isDatasetLoading) {
    return <BlockSpinner containerProps={{ height: "100%" }} />;
  }

  return (
    <>
      <DrawHandler
        initialFeatures={initialFeatures}
        startingMode={initialFeatures.length > 0 ? "simple_select" : "passing_draw_polygon"}
        sources={sources}
        styles={areaDrawStyles}
        modes={modes}
        config={carveToolbarConfig}
        disableHoverEffects
      />
      <StackedNavigationHeader onBackButtonClick={handleBack} title="Identify subareas " />
      <Box data-testid="draw-panel" sx={{ p0: 2 }}>
        {rowData?.length === 0 && autoCarving === "Idle" && (
          <Alert severity="info" data-testid="layer-alert" sx={{ fontSize: (theme) => theme.typography.size.small }}>
            <AlertTitle>Define your work area</AlertTitle>
            Draw <HighlightAlt style={{ width: 16, height: 16, verticalAlign: "middle" }} /> or upload{" "}
            <CloudUpload style={{ width: 16, height: 16, verticalAlign: "middle" }} /> your area of interest. FOND will use this to auto-generate
            smaller subareas of up to {MAX_NUM_ADDRESSES.toLocaleString()} premises each
          </Alert>
        )}
        {rowData && rowData.length > 0 && (
          <SubareaConfigPanel
            key={`${subareaMode}-${combineTargetSize}`}
            subareaMode={subareaMode}
            combineTargetSize={combineTargetSize}
            onChange={handleSubareaConfigChange}
            disabled={autoCarving !== "Idle"}
          />
        )}
        <AreaList onReset={onReset} rowData={rowData} autoCarving={autoCarving} />
        <Box display="flex" alignItems="center" justifyContent="flex-end" mt={2}>
          <Box>
            <Button color="primary" size="small" onClick={handleBack}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              size="small"
              onClick={handleOnImport}
              sx={{ ml: 1, px: 2 }}
              data-testid="finish-button"
              loading={isSaving}
              disabled={autoCarving !== "Idle"}
            >
              Finish
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AreaDrawPanel;
